import React from 'react';
import KpiCardsSection from '../../components/kpicardssection';
import PrintPageWrapper from '../../components/printpagewrapper';
import lang from '../../languages/en.json';
import styles from './OperationsPrintView.module.css';
import { Card, KpiGrid, TabData } from '../../models/operationsmodels/KpiModels';
import { getGaugeColor } from '../../utils/gaugeUtils';
import { selectAllOperationsGridsInOrder } from '../../redux/OperationsReducer/operationsSlice';
import { useAppDispatch } from '../../redux/hooks';
import { setIsMultiStoreEnabled } from '../../redux/UserReducer/userSlice';
import { buildStoreHeaderInfo } from '../../utils/appUtils';
import Inventory from './tabs/inventory/Inventory';
import B2B from './tabs/b2b/B2B';
import Marketing from './tabs/marketing/Marketing';
import People from './tabs/people/People';
import { UserDetails } from '../../models/UserModel';
import type { RootState } from '../../redux/store';

// Container ID used by waitForPerformanceRenderComplete to poll for readiness
const PRINT_CONTAINER_ID = 'operations-print-container';

// Number of skeleton grid rows shown during loading
const SKELETON_GRID_COUNT = 3;
// Number of skeleton KPI card placeholders shown during loading
const SKELETON_CARD_COUNT = 4;

/** Type for the Redux store exposed on window by store.ts */
interface WindowWithStore extends Window {
  __REDUX_STORE__?: {
    getState: () => RootState;
  };
}

/**
 * Reads the parent tab's Redux state via window.opener.__REDUX_STORE__.
 * Returns null if the parent tab has been closed or the store is unavailable.
 */
const getParentState = (): RootState | null => {
  try {
    const opener = window.opener as WindowWithStore | null;
    return opener?.__REDUX_STORE__?.getState() ?? null;
  } catch {
    // Cross-origin or closed tab — can't access opener
    return null;
  }
};

/** Snapshot of data read from the parent tab's Redux store */
interface ParentSnapshot {
  userDetails: UserDetails | null;
  isMultiStoreEnabled: boolean;
  kpiCards: Card[];
  allGrids: KpiGrid[];
}

/**
 * OperationsPrintView
 *
 * Standalone page rendered at /print-operations in a new browser tab.
 * Reads Operations data directly from the parent tab's Redux store via
 * window.opener — no duplicate API calls needed.
 *
 * If the parent tab is closed before this page loads, an error message
 * is shown telling the user to go back and click Print again.
 */
const OperationsPrintView: React.FC = (): JSX.Element => {
  const [snapshot, setSnapshot] = React.useState<ParentSnapshot | null>(null);
  const [isParentUnavailable, setIsParentUnavailable] = React.useState<boolean>(false);
  const dispatch = useAppDispatch();

  // Read parent tab's Redux state on mount
  React.useEffect(() => {
    const parentState = getParentState();
    if (!parentState) {
      setIsParentUnavailable(true);
      return;
    }

    const { operations, user } = parentState;

    // Use the canonical selector so card-order and per-card grid-order
    // always match the live Operations view exactly.
    const orderedGrids = selectAllOperationsGridsInOrder(parentState);

    // Sync the parent tab's multi-store flag into this print window's own Redux
    // store so components that read `selectIsMultiStoreEnabled` render correctly.
    dispatch(setIsMultiStoreEnabled(user.isMultiStoreEnabled));

    setSnapshot({
      userDetails: user.userDetails,
      isMultiStoreEnabled: user.isMultiStoreEnabled,
      kpiCards: operations.kpiDashboard?.cards ?? [],
      allGrids: orderedGrids,
    });
  }, [dispatch]);

  const { userDetails, isMultiStoreEnabled, kpiCards, allGrids } = snapshot ?? {
    userDetails: null,
    isMultiStoreEnabled: false,
    kpiCards: [],
    allGrids: [],
  };

  // Split allGrids by card name for passing to live section components
  const inventoryGrids = React.useMemo(
    () => allGrids.filter(g => g.cardName?.toUpperCase() === 'INVENTORY'),
    [allGrids],
  );
  const marketingGrids = React.useMemo(
    () => allGrids.filter(g => g.cardName?.toUpperCase() === 'MARKETING'),
    [allGrids],
  );
  const peopleGrids = React.useMemo(
    () => allGrids.filter(g => g.cardName?.toUpperCase() === 'PEOPLE'),
    [allGrids],
  );
  const b2bGrids = React.useMemo(
    () => allGrids.filter(g => g.cardName?.toUpperCase() === 'B2B'),
    [allGrids],
  );

  const reportDate = React.useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, []);

  // Filename: Operations_Report_[StoreNumber]_[YYYY-MM-DD].pdf
  const downloadFilename = React.useMemo(() => {
    const storeNumber = userDetails?.currentStoreNumber || 'unknown';
    const dateStr = new Date().toISOString().split('T')[0];
    return `Operations_Report_${storeNumber}_${dateStr}.pdf`;
  }, [userDetails?.currentStoreNumber]);

  /**
   * Builds the two-line header info for the print page.
   *
   * Single store:  find currentStoreNumber in userChainList → use that store's city/state
   * Multi-store:   take userChainList[0].parentStore → find that storeNumber → use its city/state
   */
  const storeHeaderInfo = React.useMemo(
    () => buildStoreHeaderInfo(userDetails, isMultiStoreEnabled),
    [userDetails, isMultiStoreEnabled],
  );

  const handlePrint = React.useCallback(() => {
    window.print();
  }, []);

  // Set document title so browser print dialog uses the custom filename as default save name
  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = downloadFilename.replace('.pdf', '');
    return () => {
      document.title = previousTitle;
    };
  }, [downloadFilename]);

  const isContentReady = snapshot !== null;

  // Map raw Card[] to TabData[] for KpiCardsSection (content not needed in print view)
  const kpiCardTabs: TabData[] = React.useMemo(
    () =>
      kpiCards.map(card => ({
        label: card.cardTitle,
        content: null,
        current: card.currentKpi || 0,
        target: card.targetKpi || 1,
        kpiColor: getGaugeColor(card.currentKpi || 0, card.targetKpi || 1),
        hasData: card.targetKpi > 0,
      })),
    [kpiCards],
  );

  // Show error if parent tab is unavailable
  return (
    <PrintPageWrapper
      title={lang.operations.print.title}
      containerId={PRINT_CONTAINER_ID}
      isContentReady={isContentReady}
      isParentUnavailable={isParentUnavailable}
      onPrint={handlePrint}
      storeHeaderInfo={storeHeaderInfo}
      reportDate={reportDate}
      skeletonConfig={{
        cardCount: SKELETON_CARD_COUNT,
        cardHeight: 150,
        sectionCount: SKELETON_GRID_COUNT,
      }}>
      {/* KPI gauge cards */}
      <section
        className={styles.cardsSection}
        data-pdf-cards>
        <KpiCardsSection
          tabs={kpiCardTabs}
          isPrintView
        />
      </section>

      {/* Data grids — rendered via live section components with isPrintView */}
      <section className={styles.gridsSection}>
        <div data-pdf-section>
          <Inventory
            data={inventoryGrids}
            isPrintView
          />
        </div>
        <div data-pdf-section>
          <Marketing
            data={marketingGrids}
            isPrintView
          />
        </div>
        <div data-pdf-section>
          <People
            data={peopleGrids}
            isPrintView
          />
        </div>
        <div data-pdf-section>
          <B2B
            data={b2bGrids}
            isPrintView
          />
        </div>
      </section>
    </PrintPageWrapper>
  );
};

export default OperationsPrintView;
